// COMBAT V3 — the SIGIL scorer. Pure, no DOM, no THREE, no Math.random.
//
// Two halves with different determinism contracts:
//
//   canonicalize(rawPoints)  SENDER ONLY. Float maths over the pointer samples
//                            (resample, bbox, scale). Its output is the 80-byte
//                            canonical stroke — the thing that goes on the wire.
//                            The receiver never re-runs it, so float differences
//                            between devices cannot matter here.
//
//   scoreCanonical(c, t)     BOTH CLIENTS. Integer-only on two Uint8 strokes:
//                            add, subtract, multiply, shift, and
//                            Math.floor(Math.sqrt(int)). Math.sqrt is required by
//                            IEEE-754 / ECMA-262 to be correctly rounded, so
//                            floor(sqrt(n)) for an integer n < 2^53 is identical
//                            on every engine. No trig: rotation tolerance uses a
//                            baked integer cos/sin table.
//
// Templates are canonicalized with the same function as strokes, but they are
// static data computed with only + - * / sqrt and Math.round — operations that
// are exactly specified — so every client derives the same template bytes.
// scripts/sigil-test.mjs pins a checksum of every template so a drift is loud.

export const N = 40;                 // resampled points per stroke
export const BYTES = N * 2;          // 80 bytes: x0 y0 x1 y1 ... (uint8)
export const SPAN = 200;             // the longest bbox side maps to 200 of 255
export const MID = 128;
export const MIN_PATH_PX = 14;       // shorter than this is a tap, not a stroke

// Quality calibration, in uint8-frame units of mean point distance.
export const D_PERFECT = 6;          // mean error <= 6/200 of the shape = 100
export const D_ZERO = 58;            // mean error >= 58/200 of the shape = 0
export const REVERSE_PENALTY = 8;    // drawn backwards: allowed, but costs 8 units
export const ROT_STEP_PENALTY = 3;   // a 12 degree rotation costs 3 units

// cos/sin * 4096 for -12, 0, +12 degrees. Baked, never computed. Wider
// tolerance was tried (+-24) and made a 45-degree-off line read GOOD.
const ROT = [[4006, -852], [4096, 0], [4006, 852]];
const ROT_MID = 1;

// ---------------------------------------------------------------- sender side
function pathLen(pts) {
  let L = 0;
  for (let i = 1; i < pts.length; i++) {
    const dx = pts[i].x - pts[i - 1].x, dy = pts[i].y - pts[i - 1].y;
    L += Math.sqrt(dx * dx + dy * dy);
  }
  return L;
}

// Classic equidistant resample ($1-recognizer style) to exactly N points.
function resample(pts, n) {
  const L = pathLen(pts);
  if (!(L > 0)) return null;
  const step = L / (n - 1);
  const out = [{ x: pts[0].x, y: pts[0].y }];
  let acc = 0;
  let prev = { x: pts[0].x, y: pts[0].y };
  for (let i = 1; i < pts.length && out.length < n; i++) {
    let cur = pts[i];
    let dx = cur.x - prev.x, dy = cur.y - prev.y;
    let d = Math.sqrt(dx * dx + dy * dy);
    while (d > 0 && acc + d >= step && out.length < n) {
      const k = (step - acc) / d;
      const q = { x: prev.x + dx * k, y: prev.y + dy * k };
      out.push(q);
      prev = q;
      dx = cur.x - prev.x; dy = cur.y - prev.y;
      d = Math.sqrt(dx * dx + dy * dy);
      acc = 0;
    }
    acc += d;
    prev = { x: cur.x, y: cur.y };
  }
  while (out.length < n) out.push({ x: pts[pts.length - 1].x, y: pts[pts.length - 1].y });
  return out;
}

/**
 * Raw pointer samples [{x,y}] -> Uint8Array(80) canonical stroke, or null for
 * a tap / empty stroke. Translation and uniform scale are removed here, which is
 * what makes the score position- and size-invariant. Aspect ratio is KEPT on
 * purpose: a squashed triangle is a worse triangle.
 */
export function canonicalize(raw) {
  if (!raw || raw.length < 2) return null;
  const pts = [];
  for (const p of raw) {
    if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) continue;
    const last = pts[pts.length - 1];
    if (!last || last.x !== p.x || last.y !== p.y) pts.push({ x: p.x, y: p.y });
  }
  if (pts.length < 2 || pathLen(pts) < MIN_PATH_PX) return null;
  const r = resample(pts, N);
  if (!r) return null;
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const p of r) {
    if (p.x < x0) x0 = p.x; if (p.x > x1) x1 = p.x;
    if (p.y < y0) y0 = p.y; if (p.y > y1) y1 = p.y;
  }
  const side = Math.max(x1 - x0, y1 - y0) || 1;
  const s = SPAN / side;
  const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;
  const out = new Uint8Array(BYTES);
  for (let i = 0; i < N; i++) {
    out[i * 2] = Math.max(0, Math.min(255, Math.round(MID + (r[i].x - cx) * s)));
    out[i * 2 + 1] = Math.max(0, Math.min(255, Math.round(MID + (r[i].y - cy) * s)));
  }
  return out;
}

// ---------------------------------------------------------------- both sides
function isqrt(n) { return Math.floor(Math.sqrt(n)); }

// Sum of point-to-point distances, candidate rotated by ROT[r] about MID.
function sumDist(c, t, r, reverse) {
  const [cs, sn] = ROT[r];
  let sum = 0;
  for (let i = 0; i < N; i++) {
    const dx = c[i * 2] - MID, dy = c[i * 2 + 1] - MID;
    const x = MID + ((dx * cs - dy * sn + 2048) >> 12);
    const y = MID + ((dx * sn + dy * cs + 2048) >> 12);
    const j = reverse ? N - 1 - i : i;
    const ex = x - t[j * 2], ey = y - t[j * 2 + 1];
    sum += isqrt(ex * ex + ey * ey);
  }
  return sum;
}

/**
 * Integer score of a canonical stroke against a canonical template.
 * Returns { q: 0..100, d: mean error (int), rot: -1..1, rev: bool }.
 * Same bytes in -> same object out, on every client.
 */
export function scoreCanonical(c, t) {
  if (!c || !t || c.length !== BYTES || t.length !== BYTES) return { q: 0, d: 255, rot: 0, rev: false };
  let best = Infinity, bestR = ROT_MID, bestRev = false;
  for (let r = 0; r < ROT.length; r++) {
    for (let rev = 0; rev < 2; rev++) {
      const mean = Math.floor(sumDist(c, t, r, rev === 1) / N)
        + ROT_STEP_PENALTY * Math.abs(r - ROT_MID) + (rev ? REVERSE_PENALTY : 0);
      if (mean < best) { best = mean; bestR = r; bestRev = rev === 1; }
    }
  }
  return { q: qualityFromMean(best), d: best, rot: bestR - ROT_MID, rev: bestRev };
}

export function qualityFromMean(d) {
  if (d <= D_PERFECT) return 100;
  if (d >= D_ZERO) return 0;
  return Math.floor(((D_ZERO - d) * 100) / (D_ZERO - D_PERFECT));
}

// Five words, one ladder — the same words the HUD, the AI and the sims use.
export const BANDS = Object.freeze([
  { min: 90, word: 'PERFECT' }, { min: 75, word: 'GREAT' }, { min: 55, word: 'GOOD' },
  { min: 30, word: 'OKAY' }, { min: 0, word: 'MISS' },
]);
export function bandOf(q) {
  const n = Number.isInteger(q) ? q : 0;
  for (let i = 0; i < BANDS.length; i++) if (n >= BANDS[i].min) return i;
  return BANDS.length - 1;
}
export function wordOf(q) { return BANDS[bandOf(q)].word; }

// ---------------------------------------------------------------- wire format
// 80 bytes -> 108 base64 chars. The MP relay caps an action at 400 bytes of JSON;
// a full V3 attack ({type, glyph, blind, sig}) is ~150.
const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
export function encodeSig(bytes) {
  if (!bytes) return '';
  let s = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i], b = i + 1 < bytes.length ? bytes[i + 1] : 0, c = i + 2 < bytes.length ? bytes[i + 2] : 0;
    const n = (a << 16) | (b << 8) | c;
    s += B64[(n >> 18) & 63] + B64[(n >> 12) & 63]
      + (i + 1 < bytes.length ? B64[(n >> 6) & 63] : '=')
      + (i + 2 < bytes.length ? B64[n & 63] : '=');
  }
  return s;
}
export function decodeSig(str) {
  if (typeof str !== 'string' || str.length !== 108) return null;
  const out = new Uint8Array(BYTES);
  let o = 0;
  for (let i = 0; i < str.length; i += 4) {
    const v = [0, 1, 2, 3].map(k => (str[i + k] === '=' ? 0 : B64.indexOf(str[i + k])));
    if (v.some(x => x < 0)) return null;
    const n = (v[0] << 18) | (v[1] << 12) | (v[2] << 6) | v[3];
    if (o < BYTES) out[o++] = (n >> 16) & 255;
    if (o < BYTES) out[o++] = (n >> 8) & 255;
    if (o < BYTES) out[o++] = n & 255;
  }
  return out;
}
